import classNames from 'classnames/bind';
import React, { useEffect, useRef, useState } from 'react';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import StickyBetween from 'yoda-site-components/lib/components/StickyBetween/StickyBetween';
import NudgeAlert from 'yoda-checkout-components/lib/components/NudgeAlert/NudgeAlert';
import ShippingThresholdHeader from 'yoda-checkout-components/lib/components/ShippingThreshold/ShippingThresholdHeader';
import OrderSummary from 'yoda-checkout-components/lib/components/OrderSummary/OrderSummary';
import QuickScreenLazy from 'yoda-account-components/lib/components/SYFQuickScreen/QuickScreenLazy';
import { useSelector } from 'react-redux';
import {
    selectDeviceType,
    selectFeatureFlags,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import PropTypes from 'prop-types';
import styles from '../../containers/Pages/Cart/CartPageWrapper/CartPageWrapper.css';
import CartBanners from '../CartBanners/CartBanners';

const cx = classNames.bind(styles);

function OrderSummaryWrapper({
    stickyParentBlock,
    mainContainerHeightOverride,
    adjustStickyHeight,
    orderSummaryHeight,
    isNotAGiftCardOrder,
    renderFPACorBestCoupon,
    handleApplyRewardsSection,
    displayShippingThresholdHeader,
    isShippingFree,
    renderRegularBannerOverQS,
    togglePLCCDetailSlider,
}) {
    const deviceType = useSelector(selectDeviceType);
    const { isDesktop } = deviceType;
    const freeShippingThreshold =
        useSelector((state) => state.orderInfo?.freeShippingThreshold) || 0;
    const irisShippingThresholds = useSelector(
        (state) => state.irisPromotionalBannerData?.shippingThresholds || {}
    );
    const applePayEligible = useSelector((state) => state.cartDetails?.applePayEligible);
    const orderTotal =
        useSelector((state) => state.orderTotals?.['SUB TOTAL AFTER ADJUSTMENT']) || 0;
    const amountShortForShippingDiscount =
        useSelector((state) => state.cartDetails?.data?.amountShortForShippingDiscount) || 0;
    const {
        enableOrderSummaryPinning = true,
        enableNewOrderSummaryRedesign = true,
        enableShippingThresholdBanner = true,
        enableShippingThresholdProgressBar = true,
        enableFSHBanner = false,
    } = useSelector(selectFeatureFlags);
    const offSetTopParentRef = useRef(null);
    const placeholderRef = useRef(null);
    const observedElementRef = useRef(null);
    const lastScrollY = useRef(0);
    const [orderSummarySettings, setOrderSummarySettings] = useState({
        orderSummaryTop: 0,
        orderSummaryPosition: 'relative',
    });

    /* istanbul ignore next */
    const handleOrderSummaryScroll = () => {
        const orderSummaryContainer = offSetTopParentRef.current;
        const currentScrollY = window.scrollY;
        if (orderSummaryContainer) {
            // get orderSummary size and position
            const rect = orderSummaryContainer.getBoundingClientRect();

            // get content area
            const element = document.getElementById('contentContainer');
            const ele = element.getBoundingClientRect();

            // Do nothing if orderSummary fills content area height
            if (rect.height >= ele.height) {
                return;
            }

            setOrderSummarySettings((state) => {
                const newOrderSummarySettings = { ...state };
                const { orderSummaryPosition } = state;
                const isScrollingDown = currentScrollY > lastScrollY.current;

                // Unpin order summary to scroll with the page, saving the current scroll position
                const unpinOrderSummary = () => {
                    const initialTop = placeholderRef?.current?.offsetTop;
                    const currentTop = orderSummaryContainer.offsetTop;
                    const travelDiff = currentTop - initialTop;
                    newOrderSummarySettings.orderSummaryTop = Math.max(travelDiff, 0);
                    newOrderSummarySettings.orderSummaryPosition = 'relative';
                };
                // when scrolling down
                if (isScrollingDown) {
                    const windowHeight = Math.round(window.innerHeight);
                    const rectBottom = Math.round(rect.bottom);
                    const isOrderSummaryBottomVisible = rectBottom <= windowHeight;
                    // allow orderSummary to scroll until bottom is visible
                    if (!isOrderSummaryBottomVisible && orderSummaryPosition === 'sticky') {
                        unpinOrderSummary();
                    }
                    // pin orderSummary once bottom is visible
                    if (isOrderSummaryBottomVisible && orderSummaryPosition === 'relative') {
                        newOrderSummarySettings.orderSummaryTop =
                            windowHeight >= rect.height ? 0 : windowHeight - rect.height;
                        newOrderSummarySettings.orderSummaryPosition = 'sticky';
                    }
                    // when scrolling up
                } else {
                    const isOrderSummaryTopVisible = rect.top >= 0;
                    // allow orderSummary to scroll until top is visible
                    if (!isOrderSummaryTopVisible && orderSummaryPosition === 'sticky') {
                        unpinOrderSummary();
                    }
                    // pin orderSummary once top is visible
                    if (isOrderSummaryTopVisible && orderSummaryPosition === 'relative') {
                        newOrderSummarySettings.orderSummaryTop = 0;
                        newOrderSummarySettings.orderSummaryPosition = 'sticky';
                    }
                }
                // avoid rerender if state has not changed
                const hasChanged =
                    newOrderSummarySettings.orderSummaryPosition !== state.orderSummaryPosition ||
                    newOrderSummarySettings.orderSummaryTop !== state.orderSummaryTop;
                return hasChanged ? newOrderSummarySettings : state;
            });
        }
        lastScrollY.current = currentScrollY;
    };

    useEffect(() => {
        // Attach scroll event listener
        if (enableOrderSummaryPinning) {
            window.addEventListener('scroll', handleOrderSummaryScroll);
            return () => {
                window.removeEventListener('scroll', handleOrderSummaryScroll);
            };
        }
        return () => {};
    }, []);

    useEffect(() => {
        const node = observedElementRef.current;
        /* istanbul ignore next */
        if (node) {
            const resizeObserver = new ResizeObserver(() => {
                const height = node.scrollHeight + (offSetTopParentRef?.current?.offsetTop || 0);
                // Get the computed style to extract the margin values
                const elementStyles = getComputedStyle(node);
                // Parse the margin values
                const marginTop = parseInt(elementStyles.marginTop, 10) || 0;
                const marginBottom = parseInt(elementStyles.marginBottom, 10) || 0;
                const quickScreenBannerHeight = isDesktop && adjustStickyHeight ? 360 : 0;
                // Calculate the total height including margins
                const totalHeight = height + marginTop + marginBottom + quickScreenBannerHeight;
                orderSummaryHeight(totalHeight);
            });
            resizeObserver.observe(node);
            return () => {
                resizeObserver.disconnect(); // clean up
            };
        }
        return () => {};
    }, []);
    return !enableOrderSummaryPinning ? (
        <div
            className={`${dt([
                'sm:!p-0',
                'lg:!pl-2',
                'lg:!absolute',
                'lg:!w-[calc(33.33%-16px)]',
            ])} ${cx(
                'orderSummaryRedesign',
                'orderSummaryWrapper',
                'orderSummaryLarge',
                'visbileLg',
                'visbileXl',
                'lg4',
                'xl4'
            )}`}
            data-automation-id="at-ordersummarywrapper-large"
            ref={offSetTopParentRef}
        >
            <StickyBetween
                stickyParentBlock={stickyParentBlock}
                offSetTopParent={offSetTopParentRef}
                shouleObserveForResize
                mainContainerHeightOverride={mainContainerHeightOverride}
                adjustHeightForQSBanner={isDesktop && adjustStickyHeight}
            >
                <div
                    className={`${cx('orderSummaryNew', 'orderSummary')} ${dt([
                        'mb-4',
                        'smOnly:mb-1',
                        'bg-white',
                        'h-full',
                    ])}`}
                    ref={observedElementRef}
                >
                    {isNotAGiftCardOrder && renderFPACorBestCoupon(false)}
                    <div className={dt(['mb-6', 'empty:mb-0'])}>
                        <NudgeAlert />
                    </div>
                    {isNotAGiftCardOrder && deviceType.isDesktop && handleApplyRewardsSection()}
                    {enableNewOrderSummaryRedesign && (
                        <p
                            className={dt([
                                isNotAGiftCardOrder ? 'mt-6' : 'mt-4',
                                'text-xlarge',
                                'font-bold',
                                'bg-light-white',
                                'px-6',
                                'pt-5',
                                'pb-3',
                                'lg:rounded-t-lg',
                            ])}
                        >
                            Order Summary
                        </p>
                    )}
                    {/* istanbul ignore next */}
                    {displayShippingThresholdHeader && enableShippingThresholdBanner && (
                        <div
                            className={`${cx('shipping-threshold-msg')} ${dt([
                                'flex',
                                'overflow-visible',
                                'relative',
                                'justify-center',
                                'bg-light-white',
                                'p-0',
                                enableNewOrderSummaryRedesign ? 'px-6' : 'px-3',
                                (amountShortForShippingDiscount > 0 || isShippingFree) &&
                                    dt(['w-full', enableNewOrderSummaryRedesign ? 'px-6' : 'px-3']),
                            ])}`}
                        >
                            <div className={dt(['w-full'])}>
                                <ShippingThresholdHeader
                                    orderTotal={orderTotal}
                                    shippingShortTotal={amountShortForShippingDiscount}
                                    isShippingFree={isShippingFree}
                                    freeShippingThreshold={freeShippingThreshold}
                                    customClass={cx('cartShippingThreshold')}
                                    enableShippingThresholdProgressBar={
                                        enableShippingThresholdProgressBar
                                    }
                                    enableNewOrderSummaryRedesign={enableNewOrderSummaryRedesign}
                                    enableFSHBanner={enableFSHBanner}
                                    renderFrom="cartOrderSummary"
                                    irisShippingThresholds={irisShippingThresholds}
                                />
                            </div>
                        </div>
                    )}
                    <div
                        className={`${cx('row', 'orderSummaryInnerWrapper')} ${dt([
                            'px-6',
                            !enableNewOrderSummaryRedesign && 'pb-4',
                            enableNewOrderSummaryRedesign && 'sm:pb-6',
                            enableNewOrderSummaryRedesign && 'lg:pb-5',
                            // eslint-disable-next-line no-nested-ternary
                            enableNewOrderSummaryRedesign
                                ? enableShippingThresholdBanner && !displayShippingThresholdHeader
                                    ? 'pt-0'
                                    : 'pt-3'
                                : 'pt-2',
                            !isNotAGiftCardOrder && !enableNewOrderSummaryRedesign && 'pb-0',
                            !isNotAGiftCardOrder && !enableNewOrderSummaryRedesign && 'rounded-lg',
                            'rounded-b-lg',
                        ])}`}
                    >
                        <OrderSummary
                            enableNewOrderSummaryRedesign={enableNewOrderSummaryRedesign}
                            isCart
                            isSticky
                            enableApplePay={applePayEligible}
                        />
                    </div>
                    {isDesktop && renderRegularBannerOverQS && (
                        <CartBanners togglePLCCDetailSlider={togglePLCCDetailSlider} />
                    )}
                </div>
            </StickyBetween>
            {isDesktop && !renderRegularBannerOverQS ? (
                <div className={dt(['mt-1'])} data-automation-id="at-QS-Desk-Banner">
                    <QuickScreenLazy bannerType="Banner" />
                </div>
            ) : (
                ''
            )}
        </div>
    ) : (
        <>
            <div ref={placeholderRef} className={dt(['inline'])} />
            <div
                id="at-ordersummarywrapper-large"
                style={{
                    position: orderSummarySettings.orderSummaryPosition,
                    top: `${orderSummarySettings.orderSummaryTop}px`,
                    transition: 'position 0.3s ease, bottom 0.3s ease', // Optional smooth transition
                }}
                className={`${dt([
                    'sm:!p-0',
                    'lg:!pl-2',
                    'z-[4]',
                    // 'lg:!absolute',
                    // 'lg:!w-[calc(33.33%-16px)]',
                ])} ${cx(
                    'orderSummaryRedesign',
                    'orderSummaryWrapper',
                    'orderSummaryLarge',
                    'visbileLg',
                    'visbileXl',
                    'lg4',
                    'xl4'
                )}`}
                data-automation-id="at-ordersummarywrapper-large"
                ref={offSetTopParentRef}
            >
                <div
                    className={`${cx('orderSummaryNew', 'orderSummary')} ${dt([
                        'mb-4',
                        'smOnly:mb-1',
                        'bg-white',
                        'h-full',
                    ])}`}
                >
                    {isNotAGiftCardOrder && renderFPACorBestCoupon(false)}
                    <div className={dt(['mb-6', 'empty:mb-0'])}>
                        <NudgeAlert />
                    </div>
                    {isNotAGiftCardOrder && deviceType.isDesktop && handleApplyRewardsSection()}
                    <div className={dt(['lg:rounded-lg', 'bg-light-white'])}>
                        {enableNewOrderSummaryRedesign && (
                            <p
                                className={dt([
                                    isNotAGiftCardOrder ? 'mt-6' : 'mt-4',
                                    'text-xlarge',
                                    'font-bold',
                                    'bg-light-white',
                                    'px-6',
                                    'pt-5',
                                    'pb-3',
                                    'lg:rounded-t-lg',
                                ])}
                            >
                                Order Summary
                            </p>
                        )}
                        {displayShippingThresholdHeader && enableShippingThresholdBanner && (
                            <div
                                className={`${cx('shipping-threshold-msg')} ${dt([
                                    'flex',
                                    'overflow-visible',
                                    'relative',
                                    'justify-center',
                                    'bg-light-white',
                                    'p-0',
                                    enableNewOrderSummaryRedesign ? 'px-6' : 'px-3',
                                    (amountShortForShippingDiscount > 0 || isShippingFree) &&
                                        dt([
                                            'w-full',
                                            enableNewOrderSummaryRedesign ? 'px-6' : 'px-3',
                                        ]),
                                ])}`}
                            >
                                <div className={dt(['w-full'])}>
                                    <ShippingThresholdHeader
                                        orderTotal={orderTotal}
                                        shippingShortTotal={amountShortForShippingDiscount}
                                        isShippingFree={isShippingFree}
                                        freeShippingThreshold={freeShippingThreshold}
                                        customClass={cx('cartShippingThreshold')}
                                        enableShippingThresholdProgressBar={
                                            enableShippingThresholdProgressBar
                                        }
                                        enableNewOrderSummaryRedesign={
                                            enableNewOrderSummaryRedesign
                                        }
                                        enableFSHBanner={enableFSHBanner}
                                        renderFrom="cartOrderSummary"
                                        irisShippingThresholds={irisShippingThresholds}
                                    />
                                </div>
                            </div>
                        )}
                        <div
                            id="order-summary-subtotal"
                            className={`${cx('row', 'orderSummaryInnerWrapper')} ${dt([
                                'px-6',
                                !enableNewOrderSummaryRedesign && 'pb-4',
                                enableNewOrderSummaryRedesign && 'sm:pb-6',
                                enableNewOrderSummaryRedesign && 'lg:pb-5',
                                // eslint-disable-next-line no-nested-ternary
                                enableNewOrderSummaryRedesign
                                    ? enableShippingThresholdBanner &&
                                      !displayShippingThresholdHeader
                                        ? 'pt-0'
                                        : 'pt-3'
                                    : 'pt-2',
                                !isNotAGiftCardOrder && !enableNewOrderSummaryRedesign && 'pb-0',
                                !isNotAGiftCardOrder &&
                                    !enableNewOrderSummaryRedesign &&
                                    'rounded-lg',
                                'rounded-b-lg',
                            ])}`}
                        >
                            <OrderSummary
                                enableNewOrderSummaryRedesign={enableNewOrderSummaryRedesign}
                                isCart
                                isSticky
                                enableApplePay={applePayEligible}
                            />
                        </div>
                    </div>
                    {isDesktop && renderRegularBannerOverQS && (
                        <CartBanners togglePLCCDetailSlider={togglePLCCDetailSlider} />
                    )}
                </div>
                {isDesktop && !renderRegularBannerOverQS ? (
                    <div className={dt(['mt-1'])} data-automation-id="at-QS-Desk-Banner">
                        <QuickScreenLazy bannerType="Banner" />
                    </div>
                ) : (
                    ''
                )}
            </div>
        </>
    );
}

OrderSummaryWrapper.propTypes = {
    stickyParentBlock: PropTypes.element,
    mainContainerHeightOverride: PropTypes.number,
    adjustStickyHeight: PropTypes.bool,
    orderSummaryHeight: PropTypes.func,
    isNotAGiftCardOrder: PropTypes.bool,
    renderFPACorBestCoupon: PropTypes.func,
    handleApplyRewardsSection: PropTypes.func,
    displayShippingThresholdHeader: PropTypes.bool,
    isShippingFree: PropTypes.bool,
    renderRegularBannerOverQS: PropTypes.bool,
    togglePLCCDetailSlider: PropTypes.func,
};

OrderSummaryWrapper.defaultProps = {
    stickyParentBlock: '',
    mainContainerHeightOverride: null,
    adjustStickyHeight: false,
    orderSummaryHeight: () => {},
    isNotAGiftCardOrder: true,
    renderFPACorBestCoupon: () => {},
    handleApplyRewardsSection: () => {},
    displayShippingThresholdHeader: true,
    isShippingFree: false,
    renderRegularBannerOverQS: false,
    togglePLCCDetailSlider: () => {},
};

export default OrderSummaryWrapper;
